import { FundCriterionType } from '../../types/fundScreener.type';

export const FC_GROUPS = {
  general: 'General',
  performance: 'Total return performance',
  keyMeasurements: 'Key measurements',
  lipperScore: 'Lipper score',
  expenseRatios: 'Expense ratios',
};

// No distinct-currency endpoint, so hardcode the two supported markets'
// currencies; AUD is preselected when this criterion is added.
export const FUND_CURRENCY_OPTIONS = ['AUD', 'USD'];
export const DEFAULT_FUND_CURRENCY = 'AUD';
const MARKET_TO_CURRENCY: { [market: string]: string } = { AU: 'AUD', US: 'USD' };
export const getDefaultCurrencyForMarket = (market: string): string =>
  MARKET_TO_CURRENCY[market] || DEFAULT_FUND_CURRENCY;

// Cap on how many criteria can be selected in a single filter pass.
export const MAX_SELECTED_CRITERIA = 10;

// FE time options are lowercase; BE expects uppercase, including "OVERALL"
// for Lipper score's default option.
export const toApiPeriod = (timeValue: string | undefined): string | undefined =>
  timeValue ? timeValue.toUpperCase() : undefined;

// Lipper score chips (1-5 + NA) are independent toggles, not a min-max
// range — `[3, 4, 5]` is the default selection for a freshly added criterion.
export const DEFAULT_SCORE_VALUES: (number | 'NA')[] = [3, 4, 5];

const performanceTimeOptions = ['1m', '3m', '6m', '1y', '5y', '10y'];

// Key measurements offer 6M/1Y/3Y/5Y/10Y (no 1M/3M).
const keyMeasurementTimeOptions = ['6m', '1y', '3y', '5y', '10y'];

// Lipper score periods are Overall/3Y/5Y/10Y (no 1Y).
const lipperScoreTimeOptions = ['Overall', '3y', '5y', '10y'];

export const FundCriteriaList: FundCriterionType[] = [
  // General
  {
    id: 'nav',
    label: 'Latest NAV',
    tableShortLabel: 'Nav',
    group: FC_GROUPS.general,
    controlType: 'range',
    apiField: 'NAV',
  },
  // {
  //   id: 'fundCurrency',
  //   label: 'Fund currency',
  //   tableShortLabel: 'FundCcy',
  //   group: FC_GROUPS.general,
  //   // Plain top-level `CURRENCY` filter, not a RANGE/SCORE criteria.
  //   // apiField here only reads the result row's column.
  //   controlType: 'currency',
  //   apiField: 'CURRENCY',
  // },
  {
    id: 'fundCompany',
    label: 'Fund management company',
    tableShortLabel: 'FundMgmtCo',
    group: FC_GROUPS.general,
    // Plain top-level `mgmtCompanyName` filter, not a RANGE/SCORE criteria.
    // apiField here only reads the result row's column.
    apiField: 'mgmtCompanyName',
    controlType: 'search',
  },

  // Each period is its own selectable criterion (no period dropdown for this group).
  ...performanceTimeOptions.map((period) => ({
    id: `performance_${period}`,
    label: `Total return performance (${period.toUpperCase()})`,
    // Shorter form for the settings card title, e.g. "Performance (1Y)".
    shortLabel: `Performance (${period.toUpperCase()})`,
    tableShortLabel: `Perf${period.toUpperCase()}`,
    group: FC_GROUPS.performance,
    controlType: 'range' as const,
    // Period is baked into the field name (e.g. "PCT_GROWTH_1M"), not sent
    // as a separate `period` param.
    apiField: `PCT_GROWTH_${period.toUpperCase()}`,
  })),

  // Key measurements — table header short label carries the selected period
  // suffix dynamically (e.g. "Alpha" + "3Y" -> "Alpha3Y").
  {
    id: 'alpha',
    label: 'Alpha',
    tableShortLabelBase: 'Alpha',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'ALPHA',
  },
  {
    id: 'beta',
    label: 'Beta',
    tableShortLabelBase: 'Beta',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'BETA',
  },
  {
    id: 'standardDeviation',
    label: 'Standard deviation',
    tableShortLabelBase: 'StdDev',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'STANDARD_DEVIATION',
  },
  {
    id: 'sharpeRatio',
    label: 'Sharpe ratio',
    tableShortLabelBase: 'Sharpe',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'SHARP_RATIO',
  },
  {
    id: 'rSquared',
    label: 'R-squared',
    tableShortLabelBase: 'RSquare',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'R_SQUARED',
  },
  {
    id: 'correlation',
    label: 'Correlation',
    tableShortLabelBase: 'Correlation',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'CORRELATION',
  },
  {
    id: 'informationRatio',
    label: 'Information ratio',
    tableShortLabelBase: 'InfoRatio',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'INFORMATION_RATIO',
  },
  {
    id: 'riskReturn',
    label: 'Risk & return',
    tableShortLabelBase: 'RiskReturn',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'RISK_RETURN_RATIO',
  },
  {
    id: 'trackingError',
    label: 'Tracking error',
    tableShortLabelBase: 'TrackError',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'TRACKING_ERROR',
  },
  {
    id: 'variance',
    label: 'Variance',
    tableShortLabelBase: 'Variance',
    group: FC_GROUPS.keyMeasurements,
    controlType: 'range',
    timeOptions: keyMeasurementTimeOptions,
    apiField: 'VARIANCE',
  },

  // Table header suffixes the period, e.g. "TRScore3Y"; "Overall" has none.
  {
    id: 'totalReturnScore',
    label: 'Total return score',
    tableShortLabelBase: 'TRScore',
    group: FC_GROUPS.lipperScore,
    controlType: 'score',
    timeOptions: lipperScoreTimeOptions,
    apiField: 'TOTAL_RETURN_SCORE',
  },
  {
    id: 'consistentReturnScore',
    label: 'Consistent return score',
    tableShortLabelBase: 'CRScore',
    group: FC_GROUPS.lipperScore,
    controlType: 'score',
    timeOptions: lipperScoreTimeOptions,
    apiField: 'CONSISTENT_RETURN_SCORE',
  },
  {
    id: 'preservationScore',
    label: 'Preservation score',
    tableShortLabelBase: 'PresScore',
    group: FC_GROUPS.lipperScore,
    controlType: 'score',
    timeOptions: lipperScoreTimeOptions,
    apiField: 'PRESERVATION_SCORE',
  },
  {
    id: 'expensesScore',
    label: 'Expenses score',
    tableShortLabelBase: 'ExpScore',
    group: FC_GROUPS.lipperScore,
    controlType: 'score',
    timeOptions: lipperScoreTimeOptions,
    apiField: 'EXPENSE_SCORE',
  },

  // Expense ratios
  {
    id: 'totalExpenseRatio',
    label: 'Total expense ratio',
    tableShortLabel: 'TotExpRatio',
    unit: '%',
    group: FC_GROUPS.expenseRatios,
    controlType: 'range',
    apiField: 'FEE_TOTAL_EXPENSE_RATIO',
  },
  // {
  //   id: 'prospectusNetExpenseRatio',
  //   label: 'Prospectus net expense ratio',
  //   tableShortLabel: 'ProsNetExpRatio',
  //   unit: '%',
  //   group: FC_GROUPS.expenseRatios,
  //   controlType: 'range',
  //   apiField: 'FEE_PROSPECTUS_NET_EXPENSE_RATIO',
  // },
];
